import React, { useEffect, useMemo, useRef, useState } from 'react';
import { tableLabel } from './dataGraphApi';
import { KindFilterRow, withoutHiddenKinds, objectKindOf, objectKindLabel } from './objectKinds';

// One control to pick a profiled object (4.2.5, 2026-11-04): a search box whose matches drop down under
// it as you type — click one (or Enter for the first) to pick it. It replaced a search box beside a
// <select>: the box only narrowed the closed select's options, which looked like it did nothing. The
// kind filters under the box narrow the matches. The value changes only when a match is picked or the
// box is cleared; other text is the user still searching, and is put back to the value when the box
// loses focus. A source with no profiled tables (an empty `tables`) takes free text, as before.

const lower = (s) => String(s || '').toLowerCase();
export const MATCHES_SHOWN = 40;

/** The objects whose label contains `query` (case-insensitive), those that START with it first. */
export const searchObjects = (names, query) => {
  const q = lower(query.trim());
  if (!q) return names;
  const starts = [];
  const contains = [];
  names.forEach((n) => {
    const i = lower(n).indexOf(q);
    if (i === 0) starts.push(n); else if (i > 0) contains.push(n);
  });
  return [...starts, ...contains];
};

/**
 * `value` / `onChange(name)`: the picked object's label. `tables`: the graph's nodes. `listName`: what the
 * kind filters say they narrow ("Key table"). `placeholder`: for a source without profiled tables.
 */
const ObjectPicker = ({ value, onChange, tables, disabled, placeholder, testId, label, listName, kinds = true }) => {
  const [hidden, setHidden] = useState(() => new Set());
  const [text, setText] = useState(value || '');
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => { setText(value || ''); }, [value]);
  const all = useMemo(() => tables || [], [tables]);
  const free = all.length === 0;
  const visible = useMemo(() => withoutHiddenKinds(all, hidden), [all, hidden]);
  const byLabel = useMemo(() => new Map(visible.map((n) => [tableLabel(n), n])), [visible]);
  const names = useMemo(() => visible.map((n) => tableLabel(n)), [visible]);
  // The box holding the picked value is not a search yet: focusing it shows everything to browse.
  const query = lower(text.trim()) === lower(value || '') ? '' : text;
  const matches = useMemo(() => searchObjects(names, query), [names, query]);
  const exact = !free && Boolean(text.trim()) && names.some((n) => lower(n) === lower(text.trim()));
  const searching = open && !free;

  const pick = (name) => {
    setText(name);
    setOpen(false);
    if (name !== value) onChange(name);
  };
  const edit = (v) => {
    setText(v);
    setOpen(true);
    if (free) { onChange(v.trim()); return; }
    if (!v.trim() && value) onChange('');
  };
  const leave = () => {
    setOpen(false);
    if (free) return;
    const t = text.trim();
    const hit = names.find((n) => lower(n) === lower(t));
    if (hit) { if (hit !== value) onChange(hit); } else if (t) setText(value || '');
  };
  const keyDown = (e) => {
    if (free) return;
    if (e.key === 'Enter' && matches.length > 0 && text.trim()) { e.preventDefault(); pick(matches[0]); }
    if (e.key === 'Escape') { setOpen(false); setText(value || ''); }
  };

  return (
    <span className='dg-picker' ref={box}
      onBlur={(e) => { if (!box.current || !box.current.contains(e.relatedTarget)) leave(); }}>
      <input className='dg-input' value={text} disabled={disabled} aria-label={label} autoComplete='off'
        role={free ? undefined : 'combobox'} aria-expanded={searching} aria-autocomplete={free ? undefined : 'list'}
        placeholder={free ? placeholder : `Search ${all.length.toLocaleString()} objects…`}
        onChange={(e) => edit(e.target.value)} onFocus={() => setOpen(true)} onKeyDown={keyDown}
        data-testid={testId} />
      {searching && !disabled && (
        <div className='dg-picker__menu' role='listbox' data-testid={`${testId}-matches`}>
          {matches.length === 0 && <div className='dg-picker__none' data-testid={`${testId}-none`}>No object matches “{text.trim()}”.</div>}
          {matches.slice(0, MATCHES_SHOWN).map((n) => {
            const node = byLabel.get(n);
            const kind = node ? objectKindOf(node) : 'DATA';
            return (
              <button type='button' role='option' aria-selected={n === value} key={n} className={`dg-picker__item${n === value ? ' dg-picker__item--on' : ''}`}
                onMouseDown={(e) => e.preventDefault()} onClick={() => pick(n)} data-testid={`${testId}-match-${n}`}>
                <span className='dg-mono'>{n}</span>
                {kind !== 'DATA' && <span className='dg-muted'> · {objectKindLabel(kind)}</span>}
              </button>
            );
          })}
          {matches.length > MATCHES_SHOWN && (
            <div className='dg-picker__none' data-testid={`${testId}-more`}>{(matches.length - MATCHES_SHOWN).toLocaleString()} more — keep typing.</div>
          )}
        </div>
      )}
      {!free && !open && text.trim() && !exact && (
        <span className='dg-muted dg-picker__hint' data-testid={`${testId}-stale`}>Not in the latest profile.</span>
      )}
      {!free && !disabled && kinds && <KindFilterRow items={all} hidden={hidden} onChange={setHidden} listName={listName} testId={`${testId}-kinds`} />}
    </span>
  );
};

export default ObjectPicker;
